import { useCallback } from 'react';

import { Box, Button, Typography } from '@mui/material';
import type { ChatMessageErrorProps } from '@mui/x-chat';
import { useMessageContext } from '@mui/x-chat/headless';

export const RESEND_LABEL = 'Tap to resend';
export const RETRY_UPLOAD_LABEL = 'Tap to try again';
const NOT_SENT_LABEL = 'Not sent';

// Extends the slot's props so the thread can pass it through `slotProps`.
export interface FailedMessageNoteProps extends ChatMessageErrorProps {
  /** Omit when a message that did not send cannot be tried again. */
  onResend?: (messageId: string) => void;
}

/** FailedMessageNote sits under a message that did not send, offering a retry. */
export const FailedMessageNote = ({ onResend }: FailedMessageNoteProps) => {
  const { message, messageId, role, status } = useMessageContext();

  const handleResend = useCallback(
    () => onResend?.(messageId),
    [messageId, onResend],
  );

  if (status !== 'error' || role !== 'user') return null;

  const isUpload = message?.parts.some((part) => part.type === 'data-file');

  return (
    <Box
      role='alert'
      sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}
    >
      {onResend ? (
        <Button color='error' size='small' onClick={handleResend}>
          {isUpload ? RETRY_UPLOAD_LABEL : RESEND_LABEL}
        </Button>
      ) : (
        <Typography variant='body2' sx={{ color: 'error.main' }}>
          {NOT_SENT_LABEL}
        </Typography>
      )}
    </Box>
  );
};

FailedMessageNote.displayName = 'FailedMessageNote';
export default FailedMessageNote;
